import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'

export interface GeoLocation {
    id: number
    titleKz: string
    titleRu: string
    type: string
    parentId: number | null
}

export interface School {
    id: number
    titleKz: string
    titleRu: string
    regionId: number
    districtId: number | null
}

export interface MySchool {
    schoolId: number
    titleKz: string
    titleRu: string
    regionId: number
    regionTitleKz: string
    regionTitleRu: string
    districtId: number | null
    districtTitleKz: string | null
    districtTitleRu: string | null
}

export interface SchoolAdmin {
    userId: number
    firstName: string
    lastName: string
    // null у сотрудника, которому роль добавили к его аккаунту: он входит как раньше
    username: string | null
    mobile: string | null
    // null - временный пароль уже сменён
    tempPassword: string | null
    schoolId: number
}

export interface SchoolStudent {
    id: number
    firstName: string
    lastName: string
    username: string | null
    mobile: string | null
    grade: number | null
    literal: string | null
    tempPassword: string | null
}

export type ImportStatus = 'OK' | 'NO_NAME' | 'BAD_GRADE' | 'DUPLICATE_IN_FILE' | 'DUPLICATE_IN_SCHOOL' | 'MOBILE_EXIST'

export interface ImportRow {
    rowNumber: number
    status: ImportStatus
    firstName: string
    lastName: string
    grade: number | null
    literal: string | null
    student: SchoolStudent | null
}

export interface SchoolCourse {
    // null - курс школе не назначен
    courseId: number | null
    titleKz: string | null
    titleRu: string | null
}

export interface ImportResult {
    rows: ImportRow[]
    createdCount: number
}

interface Paged<T> {
    data: T[]
    pageNumber: number
    totalPages: number
    totalCount: number
}

export interface StudentListParams {
    schoolId?: number
    grade?: number
    literal?: string
    query?: string
    page?: number
    size?: number
}

export const schoolApiSlice = createApi({
    reducerPath: 'schoolApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['SchoolAdmins', 'SchoolStudents', 'SchoolCourse'],
    endpoints: (build) => ({
        getRegions: build.query<GeoLocation[], void>({
            query: () => 'geography/regions',
            transformResponse: (r: { data: GeoLocation[] }) => r.data,
        }),
        getChildLocations: build.query<GeoLocation[], number>({
            query: (regionId) => `geography/regions/${regionId}/children`,
            transformResponse: (r: { data: GeoLocation[] }) => r.data,
        }),
        searchSchools: build.query<Paged<School>, { regionId?: number; districtId?: number; query?: string; page?: number; size?: number }>({
            query: (params) => ({ url: 'geography/schools', params }),
            transformResponse: (r: { data: Paged<School> }) => r.data,
        }),

        getSchoolAdmins: build.query<SchoolAdmin[], number>({
            query: (schoolId) => `school/${schoolId}/admins`,
            transformResponse: (r: { data: SchoolAdmin[] }) => r.data,
            providesTags: ['SchoolAdmins'],
        }),
        createSchoolAdmin: build.mutation<SchoolAdmin, { schoolId: number; firstName: string; lastName: string; mobile: string }>({
            query: ({ schoolId, ...body }) => ({ url: `school/${schoolId}/admins`, method: 'POST', body }),
            transformResponse: (r: { data: SchoolAdmin }) => r.data,
            invalidatesTags: ['SchoolAdmins'],
        }),
        removeSchoolAdmin: build.mutation<void, { schoolId: number; userId: number }>({
            query: ({ schoolId, userId }) => ({ url: `school/${schoolId}/admins/${userId}`, method: 'DELETE' }),
            invalidatesTags: ['SchoolAdmins'],
        }),

        getSchoolCourse: build.query<SchoolCourse, number>({
            query: (schoolId) => `school/${schoolId}/course`,
            transformResponse: (r: { data: SchoolCourse }) => r.data,
            providesTags: ['SchoolCourse'],
        }),
        setSchoolCourse: build.mutation<SchoolCourse, { schoolId: number; courseId: number }>({
            query: ({ schoolId, courseId }) => ({ url: `school/${schoolId}/course`, method: 'PUT', body: { courseId } }),
            transformResponse: (r: { data: SchoolCourse }) => r.data,
            invalidatesTags: ['SchoolCourse'],
        }),

        getMySchools: build.query<MySchool[], void>({
            query: () => 'school/my',
            transformResponse: (r: { data: MySchool[] }) => r.data,
        }),
        getSchoolStudents: build.query<Paged<SchoolStudent>, StudentListParams>({
            query: (params) => ({ url: 'school/students', params }),
            transformResponse: (r: { data: Paged<SchoolStudent> }) => r.data,
            providesTags: ['SchoolStudents'],
        }),
        addSchoolStudent: build.mutation<ImportRow, { schoolId?: number; firstName: string; lastName: string; grade: string; literal?: string; mobile?: string; allowDuplicates?: boolean }>({
            query: (body) => ({ url: 'school/students', method: 'POST', body }),
            transformResponse: (r: { data: ImportRow }) => r.data,
            invalidatesTags: ['SchoolStudents'],
        }),
        importSchoolStudents: build.mutation<ImportResult, { file: File; schoolId?: number; dryRun: boolean; allowDuplicates: boolean }>({
            query: ({ file, schoolId, dryRun, allowDuplicates }) => {
                const body = new FormData()
                body.append('file', file)
                return {
                    url: 'school/students/import',
                    method: 'POST',
                    params: { schoolId: schoolId ?? 0, dryRun, allowDuplicates },
                    body,
                }
            },
            transformResponse: (r: { data: ImportResult }) => r.data,
            invalidatesTags: (result, error, arg) => (arg.dryRun ? [] : ['SchoolStudents']),
        }),
        updateSchoolStudent: build.mutation<SchoolStudent, { id: number; firstName?: string; lastName?: string; grade?: number; literal?: string }>({
            query: ({ id, ...body }) => ({ url: `school/students/${id}`, method: 'PUT', body }),
            transformResponse: (r: { data: SchoolStudent }) => r.data,
            invalidatesTags: ['SchoolStudents'],
        }),
        detachSchoolStudent: build.mutation<void, number>({
            query: (id) => ({ url: `school/students/${id}`, method: 'DELETE' }),
            invalidatesTags: ['SchoolStudents'],
        }),
        resetSchoolStudentPassword: build.mutation<SchoolStudent, number>({
            query: (id) => ({ url: `school/students/${id}/reset-password`, method: 'POST' }),
            transformResponse: (r: { data: SchoolStudent }) => r.data,
            invalidatesTags: ['SchoolStudents'],
        }),
    }),
})
